/** One lane on the WIG board: the goal, its measures, this week's tally, and commitments.
 *
 * Laid out in the order the book's weekly session runs — the goal, the scoreboard, then the
 * people and what they said they would do — so the lane can be read top to bottom in the
 * session without hunting.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, PlayCircle, RefreshCw, Trash2, Users } from 'lucide-react'

import { Button, ErrorState, inputClass } from '@/components/ui'
import { formatDate } from '@/lib/domain'
import { HealthChip, ScoreBar, Sparkline, Tally } from '@/features/wig/WigParts'
import {
  formatNumber,
  towards,
  useAddCommitment,
  useDeleteCommitment,
  useRecordEntry,
  type Commitment,
  type LagMeasure,
  type LeadMeasure,
  type WigLane as Lane,
} from '@/features/wig/useWig'

const SOURCE_NOTE: Record<LagMeasure['source'], string> = {
  tracker: 'Reads itself from its tracker',
  milestones: 'Reads itself from the milestones',
  manual: 'Typed in by the lane',
}

export function GoalLine({ lane, big = false }: { lane: Lane; big?: boolean }) {
  return (
    <p className={`font-heading leading-snug text-fg ${big ? 'text-2xl' : 'text-[17px]'}`}>
      From <b>{lane.fromText || '…'}</b> to <b>{lane.toText || '…'}</b> by{' '}
      <b>{lane.targetDate ? formatDate(lane.targetDate) : 'a date not yet set'}</b>.
    </p>
  )
}

function ManualLagInput({ measure }: { measure: LagMeasure }) {
  const record = useRecordEntry()
  const [value, setValue] = useState('')
  return (
    <form
      className="mt-1 flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (value.trim() === '' || Number.isNaN(Number(value))) return
        record.mutate({ measureId: measure.id, value: Number(value) }, { onSuccess: () => setValue('') })
      }}
    >
      <input
        className={`${inputClass} w-28 py-1 text-xs`}
        inputMode="decimal"
        placeholder="Today's number"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-label={`Record ${measure.name} for this week`}
      />
      <Button type="submit" disabled={record.isPending || value.trim() === ''} className="py-1 text-xs">
        Record
      </Button>
      {record.isError ? (
        <span className="text-[11px]" style={{ color: 'var(--status-red)' }}>
          {record.error instanceof Error ? record.error.message : 'Not recorded'}
        </span>
      ) : null}
    </form>
  )
}

function LagRow({ measure, canRecord }: { measure: LagMeasure; canRecord: boolean }) {
  const unit = measure.unit ? ` ${measure.unit}` : ''
  return (
    <li className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-fg">{measure.name}</span>
        <span className="font-heading text-lg leading-none text-fg">
          {formatNumber(measure.current)}
          <span className="text-sm text-fg-muted">
            {' '}
            {towards(measure)}
            {unit}
          </span>
        </span>
      </div>
      <ScoreBar lag={measure} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-fg-muted">
        <span>
          {measure.expected !== null ? `Pace says ${formatNumber(measure.expected)} by today` : 'No pace yet'}
          {' · '}
          {SOURCE_NOTE[measure.source]}
          {measure.trackerStale ? (
            <span style={{ color: 'var(--status-red)' }}> · tracker not refreshing</span>
          ) : null}
        </span>
        <Sparkline points={measure.trend} label={measure.name} width={100} height={22} />
      </div>
      {measure.source === 'manual' && canRecord ? <ManualLagInput measure={measure} /> : null}
    </li>
  )
}

function LeadRow({ measure, canRecord }: { measure: LeadMeasure; canRecord: boolean }) {
  const record = useRecordEntry()
  const target = measure.weeklyTarget ?? 0
  return (
    <li className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-fg">
          {measure.name}
          <span className="text-fg-muted">
            {' '}
            · {formatNumber(target)} a week{measure.unit ? ` (${measure.unit})` : ''}
          </span>
        </span>
        {measure.missedTwoWeeks ? (
          <span className="text-[11px] font-semibold" style={{ color: 'var(--status-amber)' }}>
            Short two weeks running
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tally
          count={measure.thisWeek}
          target={target}
          disabled={!canRecord || record.isPending}
          onSet={canRecord ? (next) => record.mutate({ measureId: measure.id, value: next }) : undefined}
        />
        <Sparkline points={measure.trend} target={target} label={measure.name} width={100} height={22} />
      </div>
      <p className="text-[11px] text-fg-muted">
        This week {formatNumber(measure.thisWeek)} · last week {formatNumber(measure.lastWeek)} of{' '}
        {formatNumber(target)}
      </p>
      {target > 12 && canRecord ? <LeadCountInput measure={measure} /> : null}
    </li>
  )
}

/** For a weekly target too large for tally boxes: type the running count. */
function LeadCountInput({ measure }: { measure: LeadMeasure }) {
  const record = useRecordEntry()
  const [value, setValue] = useState('')
  return (
    <form
      className="mt-1 flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (value.trim() === '' || Number.isNaN(Number(value))) return
        record.mutate({ measureId: measure.id, value: Number(value) }, { onSuccess: () => setValue('') })
      }}
    >
      <input
        className={`${inputClass} w-28 py-1 text-xs`}
        inputMode="numeric"
        placeholder="This week so far"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-label={`This week's count for ${measure.name}`}
      />
      <Button type="submit" disabled={record.isPending || value.trim() === ''} className="py-1 text-xs">
        Set
      </Button>
    </form>
  )
}

export function CommitmentItem({ commitment }: { commitment: Commitment }) {
  const remove = useDeleteCommitment()
  const tone =
    commitment.status === 'kept'
      ? 'var(--status-green)'
      : commitment.status === 'missed'
        ? 'var(--status-red)'
        : 'var(--fg-muted)'
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tone }} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="text-fg">{commitment.text}</span>
        <span className="block text-[11px] text-fg-muted">
          {commitment.person.displayName}
          {commitment.status !== 'open' ? ` · ${commitment.status}` : ''}
        </span>
      </span>
      {commitment.mine && commitment.status === 'open' ? (
        <button
          type="button"
          className="text-fg-muted hover:text-fg"
          aria-label="Withdraw this commitment"
          title="Withdraw — only before it is accounted for"
          onClick={() => remove.mutate(commitment.id)}
        >
          <Trash2 size={13} aria-hidden />
        </button>
      ) : null}
    </li>
  )
}

export function CommitForm({ lane }: { lane: Lane }) {
  const add = useAddCommitment()
  const [text, setText] = useState('')
  return (
    <form
      className="flex gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (!text.trim()) return
        add.mutate({ wigId: lane.id, text: text.trim() }, { onSuccess: () => setText('') })
      }}
    >
      <input
        className={`${inputClass} py-1 text-sm`}
        placeholder="One thing you will do this week that moves a lead measure"
        value={text}
        onChange={(event) => setText(event.target.value)}
        aria-label="Your commitment for this week"
      />
      <Button type="submit" disabled={!text.trim() || add.isPending}>
        Commit
      </Button>
    </form>
  )
}

export function WigLaneCard({
  lane,
  number,
  onSession,
  onEdit,
  onRetire,
}: {
  lane: Lane
  number: number
  onSession: () => void
  onEdit: () => void
  onRetire: () => void
}) {
  const people = [lane.owner.displayName, ...lane.supporters.map((s) => s.displayName)]
  return (
    <article
      className="card flex flex-col overflow-hidden"
      style={{ borderTop: '3px solid var(--fg)' }}
      aria-label={`WIG ${number}: ${lane.projectName}`}
    >
      <header className="flex items-start gap-3 border-b border-border px-4 pb-3 pt-4">
        <span className="font-heading text-5xl leading-[0.8] text-[var(--brand)]" aria-hidden>
          {number}
        </span>
        <div className="min-w-0 flex-1">
          <Link to={`/projects/${lane.projectId}`} className="font-heading text-xl text-fg hover:underline">
            {lane.projectName}
          </Link>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-fg-muted" title="Owner first, then supporters">
            <Users size={12} aria-hidden />
            <span className="truncate">
              <b className="font-semibold text-fg">{lane.owner.displayName}</b>
              {lane.supporters.length ? ` · ${people.slice(1).join(', ')}` : ''}
            </span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <HealthChip health={lane.health} />
          {lane.weeksLeft !== null ? (
            <span className="text-[11px] text-fg-muted">
              <b className="font-heading text-base text-fg">{lane.fridaysLeft}</b> Fridays left
            </span>
          ) : null}
        </div>
      </header>

      <div className="border-b border-border bg-canvas px-4 py-3">
        <p className="micro-label mb-1 text-fg-muted">Wildly important goal</p>
        <GoalLine lane={lane} />
      </div>

      <div className="space-y-4 px-4 py-4">
        <section>
          <p className="micro-label mb-2 text-fg-muted">Lag — the result</p>
          {lane.lag.length ? (
            <ul className="space-y-4">
              {lane.lag.map((measure) => (
                <LagRow key={measure.id} measure={measure} canRecord={lane.canRecord} />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-fg-muted">No result measure yet.</p>
          )}
        </section>

        <section>
          <p className="micro-label mb-2 text-fg-muted">Lead — this week</p>
          {lane.lead.length ? (
            <ul className="space-y-4">
              {lane.lead.map((measure) => (
                <LeadRow key={measure.id} measure={measure} canRecord={lane.canRecord} />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-fg-muted">
              No lead measure yet — until there is one, this WIG cannot say whether the work
              is working, and health stays a person's call.
            </p>
          )}
        </section>

        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <p className="micro-label text-fg-muted">Commitments this week</p>
            {lane.commitments.lastWeekTotal ? (
              <span className="text-[11px] text-fg-muted">
                Last week kept{' '}
                <b className="text-fg">
                  {lane.commitments.lastWeekKept} / {lane.commitments.lastWeekTotal}
                </b>
              </span>
            ) : null}
          </div>
          {lane.commitments.thisWeek.length ? (
            <ul className="mb-2 space-y-1.5">
              {lane.commitments.thisWeek.map((c) => (
                <CommitmentItem key={c.id} commitment={c} />
              ))}
            </ul>
          ) : (
            <p className="mb-2 text-xs text-fg-muted">Nobody has committed to anything this week yet.</p>
          )}
          {lane.canCommit ? <CommitForm lane={lane} /> : null}
        </section>
      </div>

      <footer className="mt-auto flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
        <Button variant="primary" onClick={onSession}>
          <PlayCircle size={14} aria-hidden />
          Session
        </Button>
        <span className="text-[11px] text-fg-muted">Weekly session: {lane.sessionDayLabel}</span>
        <span className="flex-1" />
        {lane.canEdit ? (
          <>
            <Button onClick={onEdit} title="Change the goal, measures or people">
              <Pencil size={13} aria-hidden />
              Edit
            </Button>
            <Button onClick={onRetire} title="Take the WIG off the project; its history stays">
              <RefreshCw size={13} aria-hidden />
              Retire
            </Button>
          </>
        ) : null}
      </footer>
    </article>
  )
}

export function RetireDialog({
  lane,
  onClose,
  retire,
}: {
  lane: Lane
  onClose: () => void
  retire: { mutate: (v: { id: string; reason: string }, o: { onSuccess: () => void }) => void; isPending: boolean; error: unknown }
}) {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Retire WIG"
      >
        <h2 className="font-heading text-2xl text-fg">Retire the {lane.projectName} WIG?</h2>
        <p className="mt-2 text-sm text-fg-muted">
          The project goes back to ordinary tracking and its health becomes a person's call
          again. Every weekly number and commitment stays on record, and the owner is free to
          own another WIG.
        </p>
        <label className="mt-4 block">
          <span className="micro-label text-fg-muted">Why — achieved, dropped, replaced?</span>
          <input className={`${inputClass} mt-1`} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
        </label>
        {retire.error ? (
          <div className="mt-3">
            <ErrorState error={retire.error} />
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || retire.isPending}
            onClick={() => retire.mutate({ id: lane.id, reason: reason.trim() }, { onSuccess: onClose })}
          >
            Retire WIG
          </Button>
        </div>
      </div>
    </div>
  )
}
